import React, { useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, CornerDownRight } from "lucide-react";

function bubbleStyle(isAdmin, dark) {
  if (isAdmin) {
    return { backgroundColor: dark ? "rgba(249,115,22,0.12)" : "#FEF0E6", borderLeft: "3px solid #F97316" };
  }
  if (dark) return { backgroundColor: "#0A1F44", border: "1px solid #1E3A8A" };
  return { backgroundColor: "#F3F4F6" };
}

function bylineColor(isAdmin, dark) {
  if (isAdmin) return "#F97316";
  return dark ? "#9CA3AF" : "#6B7280";
}

export function ReplyBubble({ author, isAdmin, text, date, dark, testId }) {
  return (
    <div data-testid={testId} className="rounded-lg px-3 py-2" style={bubbleStyle(isAdmin, dark)}>
      <p className={`text-sm ${dark ? "text-white" : "text-[#1A1A1A]"} leading-relaxed whitespace-pre-line`}>{text}</p>
      <p className="text-[11px] mt-1 font-medium" style={{ color: bylineColor(isAdmin, dark) }}>
        {isAdmin ? "360 Safety Systems" : author} · {new Date(date).toLocaleString()}
      </p>
    </div>
  );
}

export function ReplyToggle({ onSend, dark, testPrefix, placeholder = "Write a reply..." }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!text.trim()) { toast.error("Please type a reply"); return; }
    setBusy(true);
    try {
      await onSend(text.trim());
      setText(""); setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send reply");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button data-testid={`${testPrefix}-toggle`} onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition-colors hover:border-[#F97316] hover:text-[#F97316]"
        style={dark ? { borderColor: "#1E3A8A", color: "#9CA3AF" } : { borderColor: "#E5E7EB", color: "#6B7280" }}>
        <CornerDownRight size={13} /> Reply
      </button>
    );
  }
  return (
    <div className="flex gap-2 items-start" data-testid={`${testPrefix}-box`}>
      <textarea data-testid={`${testPrefix}-input`} value={text} onChange={(e) => setText(e.target.value)} rows={2}
        placeholder={placeholder} autoFocus
        className="flex-1 rounded-lg px-3 py-2 text-sm outline-none resize-y"
        style={dark ? { backgroundColor: "#0A1F44", border: "1px solid #1E3A8A", color: "#fff" } : { border: "1px solid #E5E7EB" }} />
      <button data-testid={`${testPrefix}-send`} onClick={send} disabled={busy}
        className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
        style={{ backgroundColor: "#F97316" }}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send
      </button>
    </div>
  );
}

export function FeedbackThread({ replies, onSend, dark, testPrefix }) {
  const hasReplies = (replies || []).length > 0;
  if (!hasReplies && !onSend) return null;
  return (
    <div className="mt-3 space-y-2 pl-3 border-l-2" style={{ borderColor: dark ? "#1E3A8A" : "#FDBA74" }} data-testid={`${testPrefix}-thread`}>
      {(replies || []).map((r) => (
        <ReplyBubble key={r.id} author={r.name} isAdmin={r.author_role === "admin"} text={r.text} date={r.created_at} dark={dark} testId={`${testPrefix}-msg-${r.id}`} />
      ))}
      {onSend && <ReplyToggle onSend={onSend} dark={dark} testPrefix={`${testPrefix}-reply`} />}
    </div>
  );
}
